import React, { useState, useEffect } from "react";
import { Icon } from "../icons.jsx";
import {
  Card, Button, Badge, Field, Check, Radio, Switch, Segmented, Modal,
  Donut, MultiDonut, BarChart, Slider,
} from "../components.jsx";
import { useStore } from "../store.jsx";
import { TALENTION_HELPERS } from "../data.js";

import { PageHead } from "./dashboard.jsx";
import { KPIMini } from "./orgchart.jsx";

/* ============================================================
   Talention — Escalas (rangos de puntuación)
   ============================================================ */

function Scales() {
  const { state, dispatch } = useStore();
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const sorted = [...state.scales].sort((a, b) => b.min - a.min);
  const maxRange = Math.max(...state.scales.map(s => s.max));

  // group positions by scale
  const positionsByScale = {};
  const counts = {};
  state.positions.forEach(p => {
    if (p.status !== "FINALIZADA") return;
    const s = TALENTION_HELPERS.findScaleForPoints(p.points, state.scales);
    if (s) {
      counts[s.id] = (counts[s.id] || 0) + 1;
      if (!positionsByScale[s.id]) positionsByScale[s.id] = [];
      positionsByScale[s.id].push(p);
    }
  });

  return (
    <div className="stack" style={{ gap: 22 }}>
      <PageHead
        title="Escalas Salariales"
        sub="Cada rango de puntuación se localiza en una escala. Las escalas determinan la banda salarial del puesto."
        actions={<Button variant="primary" icon="plus" onClick={() => setEditing("new")}>Nueva escala</Button>}
      />

      <div className="grid grid-3">
        <KPIMini label="Escalas definidas"    value={sorted.length}                                                                                      icon="scale"     color="#7c3aed" />
        <KPIMini label="Puestos clasificados" value={Object.values(counts).reduce((s, n) => s + n, 0)}                                                   icon="briefcase" color="#0891b2" />
        <KPIMini label="Sin clasificar"       value={state.positions.filter(p => p.status === "FINALIZADA").length - Object.values(counts).reduce((s, n) => s + n, 0)} icon="alert" color="#d97706" />
      </div>

      {/* Visual ladder */}
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={{
          padding: "13px 20px", background: "var(--bg-soft)", borderBottom: "1px solid var(--border)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: "var(--ink-900)", color: "white", display: "grid", placeItems: "center" }}>
              <Icon name="scale" size={14} />
            </div>
            <span style={{ fontWeight: 650, fontSize: 13.5, letterSpacing: "-0.015em" }}>Escalera de escalas</span>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ fontSize: 11.5, color: "var(--ink-400)" }}>rango 0 – {maxRange}</span>
            <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: 999, background: "var(--ink-100)", color: "var(--ink-600)", fontWeight: 600 }}>{sorted.length} escalas</span>
          </div>
        </div>
        <div style={{ padding: "12px 20px", display: "flex", flexDirection: "column", gap: 4 }}>
          {sorted.map((s, i) => {
            const segWidth = ((s.max - s.min + 1) / maxRange) * 100;
            const leftPct = (s.min / maxRange) * 100;
            const count = counts[s.id] || 0;
            return (
              <div key={s.id} style={{ display: "flex", gap: 14, padding: "8px 4px", alignItems: "center" }}>
                <div className="row" style={{ gap: 10, minWidth: 170, flexShrink: 0 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: s.color + "18", color: s.color,
                    display: "grid", placeItems: "center",
                    fontWeight: 700, fontSize: 11.5,
                    flexShrink: 0,
                    border: `1px solid ${s.color}25`,
                  }}>{s.id < 10 ? "0" + s.id : s.id}</div>
                  <div>
                    <div className="semi" style={{ fontSize: 13.5 }}>{s.name}</div>
                    <div className="tiny muted">{s.label}</div>
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ position: "relative", height: 26 }}>
                    <div style={{ position: "absolute", inset: 0, background: "var(--ink-100)", borderRadius: 6 }} />
                    <div style={{
                      position: "absolute", left: leftPct + "%", width: segWidth + "%",
                      top: 0, bottom: 0,
                      background: `linear-gradient(90deg, ${s.color}cc, ${s.color})`,
                      borderRadius: 6,
                      boxShadow: "0 1px 0 rgba(0,0,0,0.05) inset",
                    }} />
                  </div>
                  <div style={{ position: "relative", height: 16, marginTop: 2 }}>
                    <span style={{
                      position: "absolute", left: leftPct + "%",
                      fontSize: 10.5, fontFamily: "var(--font-mono)",
                      color: "var(--ink-400)", whiteSpace: "nowrap",
                    }}>{s.min}</span>
                    <span style={{
                      position: "absolute", left: Math.min(leftPct + segWidth, 98) + "%",
                      transform: "translateX(-100%)",
                      fontSize: 10.5, fontFamily: "var(--font-mono)",
                      color: "var(--ink-400)", whiteSpace: "nowrap",
                    }}>{s.max}</span>
                  </div>
                </div>
                <div className="row" style={{ gap: 8, minWidth: 110, justifyContent: "flex-end", flexShrink: 0 }}>
                  <Badge tone={count > 0 ? "good" : ""}>{count} puesto{count !== 1 ? "s" : ""}</Badge>
                  <button className="icon-btn" onClick={() => setEditing(s)}><Icon name="edit" size={14} /></button>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Table view */}
      <Card className="flush">
        <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)" }}>
          <div className="card-title">Detalle de escalas</div>
        </div>
        <table className="t-table">
          <thead><tr>
            <th style={{ width: 60 }}>ID</th>
            <th>Denominación</th>
            <th>Banda</th>
            <th>Rango Inicial</th>
            <th>Rango Final</th>
            <th>Color</th>
            <th>Puestos en escala</th>
            <th style={{ width: 100 }}>Acciones</th>
          </tr></thead>
          <tbody>
            {sorted.map(s => (
              <tr key={s.id}>
                <td><span className="row-id">#{s.id}</span></td>
                <td><b>{s.name}</b></td>
                <td className="muted">{s.label}</td>
                <td className="mono semi">{s.min}</td>
                <td className="mono semi">{s.max}</td>
                <td>
                  <div className="scale-chip">
                    <span className="swatch" style={{ background: s.color }} />
                    <span className="mono tiny">{s.color}</span>
                  </div>
                </td>
                <td><Badge tone={counts[s.id] ? "good" : ""}>{counts[s.id] || 0}</Badge></td>
                <td>
                  <div className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
                    {counts[s.id] > 0 && <button className="icon-btn" title="Ver puestos" onClick={() => setViewing(s)}><Icon name="eye" size={14} /></button>}
                    <button className="icon-btn" onClick={() => setEditing(s)}><Icon name="edit" size={14} /></button>
                    <button className="icon-btn" onClick={() => {
                      if (confirm(`¿Eliminar la escala "${s.name}"?`)) dispatch({ type: "DELETE_SCALE", id: s.id });
                    }}><Icon name="trash" size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <ScaleModal
        open={editing !== null}
        scale={editing === "new" ? null : editing}
        onClose={() => setEditing(null)}
        onSave={(data) => {
          if (editing === "new") dispatch({ type: "ADD_SCALE", scale: data });
          else dispatch({ type: "UPDATE_SCALE", id: editing.id, patch: data });
          setEditing(null);
        }}
      />

      <ScalePositionsModal
        open={viewing !== null}
        scale={viewing}
        positions={viewing ? (positionsByScale[viewing.id] || []) : []}
        state={state}
        onClose={() => setViewing(null)}
      />
    </div>
  );
}

function ScaleModal({ open, scale, onClose, onSave }) {
  const [name, setName] = useState("");
  const [label, setLabel] = useState("");
  const [min, setMin] = useState(0);
  const [max, setMax] = useState(100);
  const [color, setColor] = useState("#0a84ff");
  useEffect(() => {
    setName(scale?.name || "");
    setLabel(scale?.label || "");
    setMin(scale?.min ?? 0);
    setMax(scale?.max ?? 100);
    setColor(scale?.color || "#0a84ff");
  }, [open, scale]);

  const presets = ["#16a34a", "#eab308", "#7c3aed", "#dc2626", "#0ea5e9", "#ec4899", "#f97316", "#1e3a8a", "#06b6d4", "#059669", "#a16207", "#6b7280"];

  return (
    <Modal open={open} onClose={onClose} title={scale ? "Editar escala" : "Nueva escala"}
      width={560}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" onClick={() => name.trim() && onSave({ name, label, min: parseInt(min), max: parseInt(max), color })}>
            {scale ? "Guardar" : "Crear escala"}
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 14 }}>
        <div className="grid grid-2">
          <Field label="Denominación"><input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="Ej. Escala 01" autoFocus /></Field>
          <Field label="Etiqueta de banda"><input className="input" value={label} onChange={e => setLabel(e.target.value)} placeholder="Ej. Directivo" /></Field>
        </div>
        <div className="grid grid-2">
          <Field label="Rango inicial"><input type="number" className="input" value={min} onChange={e => setMin(e.target.value)} /></Field>
          <Field label="Rango final"><input type="number" className="input" value={max} onChange={e => setMax(e.target.value)} /></Field>
        </div>
        <Field label="Color">
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            {presets.map(c => (
              <button key={c} onClick={() => setColor(c)}
                style={{
                  width: 32, height: 32, borderRadius: 8, background: c,
                  border: color === c ? "3px solid var(--brand-500)" : "1px solid var(--border)",
                  cursor: "pointer", padding: 0,
                }} />
            ))}
            <input type="color" value={color} onChange={e => setColor(e.target.value)}
              style={{ width: 32, height: 32, border: "1px solid var(--border)", borderRadius: 8, cursor: "pointer", padding: 0 }} />
          </div>
        </Field>
        <div className="banner info" style={{ marginTop: 6 }}>
          <Icon name="info" size={16} className="banner-icon" />
          <div className="tiny" style={{ flex: 1 }}>
            Cualquier puesto cuya puntuación SVPT total esté entre <b>{min}</b> y <b>{max}</b> se asignará a esta escala automáticamente.
          </div>
        </div>
      </div>
    </Modal>
  );
}

function ScalePositionsModal({ open, scale, positions, state, onClose }) {
  if (!scale) return null;
  return (
    <Modal open={open} onClose={onClose} title={`Puestos en ${scale.name}`} width={560}
      footer={<Button variant="ghost" onClick={onClose}>Cerrar</Button>}
    >
      <div className="stack" style={{ gap: 10 }}>
        <div className="row" style={{ gap: 10, padding: "10px 14px", borderRadius: 12, background: scale.color + "12", border: `1px solid ${scale.color}25` }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: scale.color + "25", color: scale.color, display: "grid", placeItems: "center", fontWeight: 700, fontSize: 12 }}>
            {positions.length}
          </div>
          <div>
            <div className="semi" style={{ fontSize: 14, color: scale.color }}>{scale.name} · {scale.label}</div>
            <div className="tiny muted">Rango {scale.min} – {scale.max} pts</div>
          </div>
        </div>

        {positions.length === 0 ? (
          <div className="empty">Sin puestos clasificados en esta escala</div>
        ) : (
          <table className="t-table">
            <thead><tr>
              <th style={{ width: 80 }}>Código</th>
              <th>Puesto</th>
              <th>Departamento</th>
              <th style={{ width: 80 }}>Pts SVPT</th>
            </tr></thead>
            <tbody>
              {positions.map(p => {
                const dept = state.departments.find(d => d.id === p.departmentId);
                return (
                  <tr key={p.id}>
                    <td><span className="row-id mono">{p.code}</span></td>
                    <td><b>{p.name}</b></td>
                    <td className="muted">{dept?.name || "—"}</td>
                    <td><span className="mono semi" style={{ color: scale.color }}>{p.points}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </Modal>
  );
}

export { Scales };